/**
 * Version registry for onboarding guides.
 *
 * When a new major version ships:
 *   1. Create src/features/onboarding/config/steps-v3.ts  (copy steps-v2, update content)
 *   2. Add an entry here:  3: { majorVersion: 3, steps: GUIDE_STEPS_V3 }
 *   3. Ship.  Existing users see the new guide automatically on next login.
 */

import { appVersion } from '@/config';
import { getStoredItem, saveItem } from '@/utils/storage';

import { GUIDE_STEPS_V2 } from './steps';
import { GUIDE_STEPS_V27 } from './steps-v27';
import { GUIDE_STEPS_V29 } from './steps-v29';
import type { GuideVersion, RouteScope } from './types';

const STORAGE_PREFIX = 'taksoai_onboarding_v';

/** Parse the semver major.minor out of "taksoai-app@2.6.78" → "2.6" */
export const getVersionKey = (ver: string): string => {
  const match = ver.match(/@(\d+)\.(\d+)/);
  return match ? `${match[1]}.${match[2]}` : '0.0';
};

const getStorageKey = () => `${STORAGE_PREFIX}${getVersionKey(appVersion)}`;

/**
 * Returns true if the user has already completed or skipped the guide for the current major version.
 * TODO: what about we have another big major version, and user have already checked previous version?
 */
export const hasSeenCurrentGuide = (): boolean =>
  getStoredItem(getStorageKey()) === 'true';

/** Persists that the user has seen the guide for the current major version. */
export const markGuideAsSeen = () => saveItem(getStorageKey(), 'true');

/**
 * Exposed GUIDE CONFIG for other components!!
 * CAN REFER TO SUBSEQUENT VERSION STEPS!
 *
 * TODO: dynamic guide based on user status?
 * - if total new user, need a full version steps;
 * - if existing user and got new major version, need incremental version;
 */
export const CURRENT_GUIDE = GUIDE_STEPS_V29;

export const GUIDE_VERSIONS: Record<string, GuideVersion> = {
  '2.6': { majorVersion: 2, steps: GUIDE_STEPS_V2 },
  '2.7': { majorVersion: 2, steps: GUIDE_STEPS_V27 },
  // 2.8 skipped, totally same as 2.9
  '2.9': { majorVersion: 2, steps: GUIDE_STEPS_V29 },
};

/** Returns the guide for a given semver major.minor key, or null if none is registered. */
export const resolveGuide = (versionKey: string): GuideVersion | null =>
  GUIDE_VERSIONS[versionKey] ?? null;

// ─── Route-scope helpers ────────────────────────────────────────────────────

/** Human-readable page name shown in navigation prompts. */
export const ROUTE_SCOPE_LABELS: Record<RouteScope, string> = {
  home: 'Projects Home',
  'project-detail': 'Project Detail',
  drawing: 'Drawing Page',
};

/**
 * Returns true when `pathname` belongs to the given `scope`.
 *
 * Matching rules:
 *   home           → starts with /home
 *   project-detail → matches /project/:id exactly (no further segments)
 *   drawing        → contains /file/ (drawing canvas route)
 */
export const matchesScope = (pathname: string, scope: RouteScope): boolean => {
  switch (scope) {
    case 'home':
      return pathname.startsWith('/home');
    case 'project-detail':
      // /project/123  ✓     /project/123/file/456/page/789  ✗
      return /^\/project\/[^/]+\/?$/.test(pathname);
    case 'drawing':
      return pathname.includes('/file/');
  }
};

export * from './types';
export * from './steps';
